import type { BqInsightsResponse, BqKpiDay, BqRetentionPoint } from '@/types';

/** BqKpiDay fields that hold numbers. */
export type BqKpiKey = Exclude<keyof BqKpiDay, 'date'>;

export interface LatestVsAverage {
  /** The last day's value; for D1/D7 the last install day whose value is known. */
  latest: number | null;
  latestDate: string | null;
  /** Average of up to 7 known values before `latest`; null with fewer than 3. */
  avg7: number | null;
  /** (latest − avg7) / avg7 × 100; null when either is unknown or avg7 is 0. */
  changePct: number | null;
}

const known = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export function latestVsAverage(kpis: BqKpiDay[], key: BqKpiKey): LatestVsAverage {
  let i = kpis.length - 1;
  while (i >= 0 && !known(kpis[i][key])) i--;
  if (i < 0) return { latest: null, latestDate: null, avg7: null, changePct: null };

  const latest = kpis[i][key] as number;
  const prior: number[] = [];
  for (let j = i - 1; j >= 0 && prior.length < 7; j--) {
    const v = kpis[j][key];
    if (known(v)) prior.push(v);
  }
  const avg7 = prior.length >= 3 ? prior.reduce((a, b) => a + b, 0) / prior.length : null;
  const changePct = avg7 !== null && avg7 !== 0 ? ((latest - avg7) / avg7) * 100 : null;
  return { latest, latestDate: kpis[i].date, avg7, changePct };
}

/** The empty-value dash, shared with the Monetization page. */
export const DASH = '—';

/** 0.2547 → '25.5%'; null → '—'. */
export function fmtPct(v: number | null): string {
  return v === null ? DASH : `${(v * 100).toFixed(1)}%`;
}

/** $100 and up as whole dollars with separators, else `decimals` (default 2); null → '—'. */
export function fmtUsd(v: number | null, decimals = 2): string {
  if (v === null) return DASH;
  if (Math.abs(v) >= 100) return `$${Math.round(v).toLocaleString('en-US')}`;
  return `$${v.toFixed(decimals)}`;
}

/** 312593 → '312,593'; 21.456 with 1 decimal → '21.5'; null → '—'. */
export function fmtNum(v: number | null, decimals = 0): string {
  if (v === null) return DASH;
  return v.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** GA4 day 'YYYY-MM-DD' → '18 Oct'. */
export function dayLabel(date: string): string {
  const [, month, day] = date.split('-').map(Number);
  return `${day} ${MONTHS[month - 1]}`;
}

export interface RetentionRow {
  cohortDate: string;
  cohortSize: number;
  /** Retention rate by day N; a day the cohort has not reached yet is absent. */
  rates: Record<number, number | null>;
  /** Cumulative revenue per install at `ltvDay`, the latest day N with an LTV. */
  ltvUsd: number | null;
  ltvDay: number | null;
}

/** v_retention points → one row per install day, newest first. */
export function retentionMatrix(points: BqRetentionPoint[]): RetentionRow[] {
  const rows = new Map<string, RetentionRow>();
  for (const p of points) {
    let row = rows.get(p.cohortDate);
    if (!row) {
      row = {
        cohortDate: p.cohortDate,
        cohortSize: p.cohortSize,
        rates: {},
        ltvUsd: null,
        ltvDay: null,
      };
      rows.set(p.cohortDate, row);
    }
    row.rates[p.dayN] = p.retentionRate;
    if (known(p.ltvUsd) && (row.ltvDay === null || p.dayN > row.ltvDay)) {
      row.ltvUsd = p.ltvUsd;
      row.ltvDay = p.dayN;
    }
  }
  return [...rows.values()].sort((a, b) => b.cohortDate.localeCompare(a.cohortDate));
}

/** A platform or version filter is set: KPIs, trends, retention and FTUE follow it; the rest is the whole game. */
export const isFiltered = (data: BqInsightsResponse): boolean =>
  (data.platform ?? 'all') !== 'all' || data.version != null;
